import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Bell, Award, Briefcase, CheckCheck } from "lucide-react";

const NotificationsBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setData((await api.get("/notifications")).data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    try {
      await api.post("/notifications/read");
      load();
    } catch { /* ignore */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell" className="relative ml-auto rounded-full p-2 text-gray-500 hover:bg-gray-100">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF4500] px-1 text-[10px] font-black text-white">
              {data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <span className="text-sm font-extrabold text-gray-900">Notifications</span>
          {data.unread > 0 && (
            <button onClick={markAll} data-testid="notifications-mark-read"
              className="inline-flex items-center gap-1 text-xs font-bold text-[#002855] hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.notifications.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-gray-400" data-testid="notifications-empty">
              All clear — no alerts right now.
            </p>
          )}
          {data.notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => { setOpen(false); navigate(n.url || "/feed"); }}
              data-testid={`notification-${n.id}`}
              className="flex w-full items-start gap-3 border-b border-gray-50 px-4 py-3 text-left hover:bg-gray-50"
            >
              <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${n.type === "application" ? "bg-blue-50" : "bg-amber-50"}`}>
                {n.type === "application" ? <Briefcase className="h-4 w-4 text-[#002855]" /> : <Award className="h-4 w-4 text-amber-600" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-gray-800">{n.title}</span>
                <span className="block text-xs text-gray-500">{n.body}</span>
              </span>
              {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#FF4500]" />}
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsBell;
